import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { RestaurantHistoryResponse } from "../utils/RestoApiService";
import { UserReport } from "./UserReportsUtils";
import { DateFormatter } from "../utils/DateFormatter";
import { getEmojiForScore } from "../utils/EmojiUtils";
import { BellIcon } from "./Icon";
import SubscriptionModal from "./SubscriptionModal";
import SubscriberCount from "./SubscriberCount";
import AnalyticsManager from "../utils/AnalyticsManager";

interface CombinedRatingsTimelineProps {
  historyData?: RestaurantHistoryResponse;
  userReports: UserReport[];
  onReportClick?: (reportId: string) => void;
  subscriberCount: number;
  isLoadingSubscriberCount: boolean;
  subscriberCountError: string | null;
}

const getStatusInfo = (status: string, t: (key: string) => string): { color: string; label: string } => {
  switch (status) {
    case "PASSED_INSPECTION":
      return { color: "text-green-500", label: t('restaurantHistory.passedInspection') };
    case "WARNING":
      return { color: "text-yellow-500", label: t('restaurantHistory.receivedWarning') };
    case "OUTDATED":
      return { color: "text-gray-500", label: t('restaurantHistory.inspectionTooOld') };
    case "PENDING":
      return { color: "text-blue-500", label: t('restaurantHistory.awaitingInspection') };
    case "FINE":
      return { color: "text-orange-500", label: t('restaurantHistory.receivedFine') };
    case "PV":
      return { color: "text-red-500", label: t('restaurantHistory.receivedPV') };
    default:
      return { color: "text-gray-400", label: status };
  }
};

// Subscription button component
interface SubscriptionButtonProps {
  restaurantName: string;
  restaurantId: number;
}

const SubscriptionButton: React.FC<SubscriptionButtonProps> = ({ restaurantName, restaurantId }) => {
  const { t } = useTranslation();
  const [showSubscriptionModal, setShowSubscriptionModal] = React.useState(false);

  return (
    <>
      <button
        onClick={() => {
          const analytics = AnalyticsManager.getInstance();
          analytics.trackEvent('subscription_bell_click', {
            restaurant_id: restaurantId,
            restaurant_name: restaurantName,
            event_category: 'subscription'
          });
          setShowSubscriptionModal(true);
        }}
        className="relative flex items-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 active:scale-100 group"
        aria-label={t('subscription.notifyMe', 'Notify me of rating changes')}
      >
        <div className="relative">
          <BellIcon className="h-5 w-5 transition-transform duration-300 group-hover:rotate-12" />
          <span className="absolute -top-1 -right-1 h-2 w-2 bg-yellow-400 rounded-full animate-pulse"></span>
        </div>
        <span className="text-sm font-medium">{t('subscription.subscribe')}</span>
      </button>

      <SubscriptionModal
        isOpen={showSubscriptionModal}
        onClose={() => setShowSubscriptionModal(false)}
        restaurantName={restaurantName}
        restaurantId={restaurantId}
      />
    </>
  );
};

const CombinedRatingsTimeline: React.FC<CombinedRatingsTimelineProps> = ({
  historyData,
  userReports,
  onReportClick,
  subscriberCount,
  isLoadingSubscriberCount,
  subscriberCountError
}) => {
  const { t } = useTranslation();
  const [activeTooltip, setActiveTooltip] = React.useState<string | null>(null);

  // Memoize sorted data and unified date range
  const { sortedInspections, sortedReports, oldestDate, newestDate, dateRange, hasData } = useMemo(() => {
    const inspections = historyData?.entries
      ? [...historyData.entries].sort((a, b) =>
          new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
        )
      : [];

    const reports = userReports
      ? [...userReports].sort((a, b) =>
          new Date(a.date).getTime() - new Date(b.date).getTime()
        )
      : [];

    // Calculate unified date range spanning both data sources
    const allDates: Date[] = [
      ...inspections.map(e => new Date(e.timestamp)),
      ...reports.map(r => new Date(r.date))
    ];

    if (allDates.length === 0) {
      return { sortedInspections: [], sortedReports: [], oldestDate: null, newestDate: null, dateRange: 0, hasData: false };
    }

    const oldest = new Date(Math.min(...allDates.map(d => d.getTime())));
    const newest = new Date(Math.max(...allDates.map(d => d.getTime())));

    return {
      sortedInspections: inspections,
      sortedReports: reports,
      oldestDate: oldest,
      newestDate: newest,
      dateRange: newest.getTime() - oldest.getTime(),
      hasData: true
    };
  }, [historyData, userReports]);

  // Return null if no data at all
  if (!hasData || !oldestDate || !newestDate) {
    return null;
  }

  // Calculate Y position based on score (0-5)
  const getYPosition = (score: number): number => {
    return 100 - (score / 5) * 100;
  };

  // Calculate X position based on date
  const getXPosition = (dateValue: Date): number => {
    if (dateRange === 0) return 50;
    const position = ((dateValue.getTime() - oldestDate.getTime()) / dateRange) * 100;
    return Math.max(5, Math.min(95, position));
  };

  // Truncate text for preview
  const truncateText = (text: string, maxLength: number = 50): string => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  return (
    <div className="w-full">
      {/* Graph Container */}
      <div
        className="relative h-32 bg-gray-50 dark:bg-gray-800 rounded-lg p-4 mb-2"
        onClick={() => setActiveTooltip(null)}
      >
        {/* Y-axis labels */}
        <div className="absolute left-4 top-4 bottom-4 flex flex-col justify-between text-xs text-gray-500 dark:text-gray-400 w-4 text-right">
          <span className="leading-none -translate-y-[3px]">5</span>
          <span className="leading-none">4</span>
          <span className="leading-none">3</span>
          <span className="leading-none">2</span>
          <span className="leading-none">1</span>
          <span className="leading-none translate-y-[3px]">0</span>
        </div>

        {/* Graph area */}
        <div className="ml-7 h-full relative">
          {/* Grid lines */}
          <div className="absolute inset-0">
            {[0, 1, 2, 3, 4, 5].map((score) => (
              <div
                key={score}
                className="absolute w-full border-t border-gray-200 dark:border-gray-700"
                style={{ top: `${getYPosition(score)}%` }}
              />
            ))}
          </div>

          {/* SVG Lines */}
          <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
            {/* Official inspections - solid blue line */}
            {sortedInspections.length >= 1 && (
              <polyline
                points={
                  sortedInspections.length === 1
                    ? `0,${getYPosition(sortedInspections[0].score)} ${getXPosition(new Date(sortedInspections[0].timestamp))},${getYPosition(sortedInspections[0].score)}`
                    : sortedInspections.map((entry) =>
                        `${getXPosition(new Date(entry.timestamp))},${getYPosition(entry.score)}`
                      ).join(' ')
                }
                fill="none"
                stroke="rgb(59, 130, 246)"
                strokeWidth="2"
                strokeOpacity="0.6"
                vectorEffect="non-scaling-stroke"
              />
            )}

            {/* User ratings - dashed green line */}
            {sortedReports.length >= 1 && (
              <polyline
                points={
                  sortedReports.length === 1
                    ? `0,${getYPosition(sortedReports[0].rating)} ${getXPosition(new Date(sortedReports[0].date))},${getYPosition(sortedReports[0].rating)}`
                    : sortedReports.map((report) =>
                        `${getXPosition(new Date(report.date))},${getYPosition(report.rating)}`
                      ).join(' ')
                }
                fill="none"
                stroke="rgb(16, 185, 129)"
                strokeWidth="2"
                strokeOpacity="0.6"
                strokeDasharray="4 2"
                vectorEffect="non-scaling-stroke"
              />
            )}
          </svg>

          {/* Official inspection markers - blue ring */}
          {sortedInspections.map((entry, index) => {
            const xPos = getXPosition(new Date(entry.timestamp));
            const yPos = getYPosition(entry.score);
            const emoji = getEmojiForScore(entry.score);
            const date = new Date(entry.timestamp);
            const tooltipId = `official-${index}`;
            const isActive = activeTooltip === tooltipId;

            return (
              <div
                key={tooltipId}
                className={`absolute transform -translate-x-1/2 -translate-y-1/2 cursor-pointer group hover:z-50 ${isActive ? 'z-50' : 'z-10'}`}
                style={{ left: `${xPos}%`, top: `${yPos}%` }}
                role="button"
                aria-label={`Official inspection rating ${entry.score} on ${date.toLocaleDateString()}`}
                tabIndex={0}
                onClick={(e) => {
                  e.stopPropagation();
                  setActiveTooltip(isActive ? null : tooltipId);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    e.stopPropagation();
                    setActiveTooltip(isActive ? null : tooltipId);
                  }
                }}
              >
                {/* Emoji marker */}
                <div className={`text-2xl hover:scale-110 transition-transform ${
                  isActive ? 'scale-110' : ''
                }`}>
                  {emoji}
                </div>

                {/* Tooltip */}
                <div className={`absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 bg-white dark:bg-gray-900 text-gray-900 dark:text-white p-2 rounded-lg shadow-lg transition-opacity whitespace-nowrap min-w-[150px] z-50 ${
                  isActive ? 'opacity-100 pointer-events-auto' : 'opacity-0 group-hover:opacity-100 pointer-events-none'
                }`}>
                  <div className="text-xs font-medium flex items-center gap-1">
                    <span>🏛️</span>
                    {date.toLocaleDateString()}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-300">
                    {DateFormatter.formatRelativeTime(date)}
                  </div>
                  {isActive && (
                    (() => {
                      const previousScore = index > 0 ? sortedInspections[index - 1].score : null;
                      const scoreChanged = previousScore === null || previousScore !== entry.score;

                      if (!scoreChanged) {
                        return (
                          <div className="text-xs mt-1 text-gray-500 dark:text-gray-400 italic border-t border-gray-200 dark:border-gray-700 pt-1">
                            {t('restaurant.noScoreChange', 'No change in score')}
                          </div>
                        );
                      }

                      return (
                        <div className="text-xs mt-1 space-y-1 border-t border-gray-200 dark:border-gray-700 pt-1">
                          {entry.historyItems.map((item, itemIndex) => {
                            const { color, label } = getStatusInfo(item, t);
                            return (
                              <div key={itemIndex} className={color}>
                                {label}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()
                  )}
                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1">
                    <div className="border-4 border-transparent border-t-white dark:border-t-gray-900"></div>
                  </div>
                </div>
              </div>
            );
          })}

          {/* User rating markers - green ring */}
          {sortedReports.map((report) => {
            const xPos = getXPosition(new Date(report.date));
            const yPos = getYPosition(report.rating);
            const emoji = getEmojiForScore(report.rating);
            const date = new Date(report.date);
            const tooltipId = `user-${report.id}`;
            const isActive = activeTooltip === tooltipId;

            return (
              <div
                key={tooltipId}
                className={`absolute transform -translate-x-1/2 -translate-y-1/2 cursor-pointer group hover:z-50 ${isActive ? 'z-50' : 'z-10'}`}
                style={{ left: `${xPos}%`, top: `${yPos}%` }}
                role="button"
                aria-label={`User rating ${report.rating} by ${report.username} on ${date.toLocaleDateString()}`}
                tabIndex={0}
                onClick={(e) => {
                  e.stopPropagation();
                  setActiveTooltip(isActive ? null : tooltipId);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    e.stopPropagation();
                    setActiveTooltip(isActive ? null : tooltipId);
                  }
                }}
              >
                {/* Emoji marker */}
                <div className={`text-2xl hover:scale-110 transition-transform ${
                  isActive ? 'scale-110' : ''
                }`}>
                  {emoji}
                </div>

                {/* Tooltip */}
                <div className={`absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 bg-white dark:bg-gray-900 text-gray-900 dark:text-white p-2 rounded-lg shadow-lg transition-opacity whitespace-nowrap min-w-[150px] z-50 ${
                  isActive ? 'opacity-100 pointer-events-auto' : 'opacity-0 group-hover:opacity-100 pointer-events-none'
                }`}>
                  <div className="text-xs font-medium flex items-center gap-1">
                    <span>👤</span>
                    {report.username}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-300">
                    {date.toLocaleDateString()} ({DateFormatter.formatRelativeTime(date)})
                  </div>
                  {isActive && (
                    <div className="text-xs mt-1 space-y-1 border-t border-gray-200 dark:border-gray-700 pt-1">
                      <p className="text-gray-500 dark:text-gray-300 whitespace-normal max-w-[200px]">
                        {truncateText(report.comment, 80)}
                      </p>
                      {onReportClick && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onReportClick(report.id);
                          }}
                          className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                        >
                          {t("userRatingsTimeline.viewFullReport")}
                        </button>
                      )}
                    </div>
                  )}
                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1">
                    <div className="border-4 border-transparent border-t-white dark:border-t-gray-900"></div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Legend - show when both data sources exist */}
      {sortedInspections.length > 0 && sortedReports.length > 0 && (
        <div className="flex items-center justify-center gap-4 mt-1.5 mb-1">
          <div className="flex items-center gap-1.5">
            <svg width="20" height="8" className="flex-shrink-0">
              <line x1="0" y1="4" x2="20" y2="4" stroke="rgb(59, 130, 246)" strokeWidth="2" strokeOpacity="0.6" />
            </svg>
            <svg className="w-3 h-3 text-blue-500 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            </svg>
            <span className="text-[10px] text-gray-500 dark:text-gray-400">{t('restaurant.official', 'Official')}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <svg width="20" height="8" className="flex-shrink-0">
              <line x1="0" y1="4" x2="20" y2="4" stroke="rgb(16, 185, 129)" strokeWidth="2" strokeOpacity="0.6" strokeDasharray="4 2" />
            </svg>
            <svg className="w-3 h-3 text-emerald-500 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
            <span className="text-[10px] text-gray-500 dark:text-gray-400">{t('restaurant.userReports', 'User Reports')}</span>
          </div>
        </div>
      )}

      {/* Timeline dates */}
      {(sortedInspections.length > 1 || sortedReports.length > 1) && (
        <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1 ml-7 mr-4">
          <span>{oldestDate.toLocaleDateString()}</span>
          <span>{newestDate.toLocaleDateString()}</span>
        </div>
      )}

      {/* Subscriber Count */}
      <div className="mt-3 flex justify-center">
        <SubscriberCount
          count={subscriberCount}
          isLoading={isLoadingSubscriberCount}
          error={subscriberCountError}
        />
      </div>
    </div>
  );
};

export default CombinedRatingsTimeline;
export { SubscriptionButton };
